/**
 * Admin Dashboard
 * Submission queue, Staff Picks editor, and analytics
 */

'use client';

export const dynamic = 'force-dynamic';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/auth-context';
import { db } from '@/lib/firebase.client';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  doc,
  updateDoc,
  where,
  limit,
  Timestamp,
  getDocs,
  getCountFromServer
} from 'firebase/firestore';

export default function AdminPage() {
  const router = useRouter();
  const { user, isAdmin, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<'submissions' | 'picks' | 'analytics'>('submissions');

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      router.push('/');
    }
  }, [user, isAdmin, loading, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage submissions, staff picks, sponsorships, and view analytics</p>
        </div>

        {/* Quick Links Grid */}
        <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <a href="/admin/submissions" className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl hover:border-primary-300 transition-all group">
            <div className="text-4xl mb-3">📝</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600">Submissions</h3>
            <p className="text-gray-600 text-sm">Review community items</p>
          </a>
          
          <a href="/admin/sponsorships" className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl hover:border-primary-300 transition-all group">
            <div className="text-4xl mb-3">🤝</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600">Sponsorships</h3>
            <p className="text-gray-600 text-sm">Manage partnerships</p>
          </a>
          
          <a href="/admin/users" className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl hover:border-primary-300 transition-all group">
            <div className="text-4xl mb-3">👥</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600">Users</h3>
            <p className="text-gray-600 text-sm">Manage user accounts</p>
          </a>
          
          <a href="/admin/config" className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl hover:border-primary-300 transition-all group">
            <div className="text-4xl mb-3">⚙️</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600">Config</h3>
            <p className="text-gray-600 text-sm">Remote configuration</p>
          </a>
        </div>

        {/* Tabs */}
        <div className="mb-8 flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('submissions')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'submissions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Submissions
          </button>
          <button
            onClick={() => setActiveTab('picks')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'picks'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Staff Picks
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
              activeTab === 'analytics'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Analytics
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'submissions' && <SubmissionsTab />}
        {activeTab === 'picks' && <StaffPicksTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}
      </div>
    </div>
  );
}

type SourceItem = string | { url: string; label?: string };

interface Submission {
  id: string;
  title: string;
  description: string;
  productUrl?: string;
  accountType: 'FSA' | 'HSA' | 'Both';
  category?: string;
  reasoning?: string;
  sources?: SourceItem[];
  status: 'pending' | 'approved' | 'rejected';
  submittedByEmail?: string;
  eligibility?: {
    classification?: string;
    confidence?: number;
    rationale?: string;
    sources?: SourceItem[];
  };
  ogData?: {
    ogImage?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogUrl?: string;
  };
  availableUntil?: Timestamp | Date | number | null;
  createdAt?: Timestamp | Date | number;
}

// Helper function to safely format dates
function formatDate(date: Timestamp | Date | number | undefined | null): string {
  if (!date) return 'N/A';
  
  let d: Date;
  if (typeof date === 'number') {
    d = new Date(date);
  } else if (date instanceof Date) {
    d = date;
  } else if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
    d = date.toDate();
  } else {
    return 'N/A';
  }
  
  return d.toLocaleDateString();
}

function SubmissionsTab() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

  // Real-time listener for pending submissions
  useEffect(() => {
    const q = query(
      collection(db, 'submissions'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subs: Submission[] = [];
      snapshot.forEach((doc) => {
        subs.push({ id: doc.id, ...doc.data() } as Submission);
      });
      setSubmissions(subs);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching submissions:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await updateDoc(doc(db, 'submissions', id), {
        status: 'approved',
        approvedAt: new Date(),
        approvedBy: user?.uid,
      });
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await updateDoc(doc(db, 'submissions', id), {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: user?.uid,
      });
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Submission Queue</h2>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Live updates
          </div>
        </div>
        <Link 
          href="/admin/submissions"
          className="text-primary-600 hover:text-primary-700 font-medium"
        >
          View All →
        </Link>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-6xl mb-4">✅</div>
          <p className="text-gray-600 mb-2">All caught up!</p>
          <p className="text-sm text-gray-500">No pending submissions to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => (
            <div key={sub.id} className="border border-gray-200 rounded-xl p-4 hover:border-primary-300 transition-colors">
              <div className="flex gap-4">
                {sub.ogData?.ogImage && (
                  <img
                    src={sub.ogData.ogImage}
                    alt=""
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900 truncate">{sub.title}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      sub.accountType === 'Both' ? 'bg-purple-100 text-purple-700' :
                      sub.accountType === 'FSA' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {sub.accountType}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm truncate">{sub.description}</p>
                  {sub.eligibility && (
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                      sub.eligibility.classification === 'YES' ? 'bg-green-100 text-green-700' :
                      sub.eligibility.classification === 'NO' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      AI: {sub.eligibility.classification} ({Math.round((sub.eligibility.confidence || 0) * 100)}%)
                    </span>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => setSelectedSubmission(sub)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    👁️
                  </button>
                  <button
                    onClick={() => handleApprove(sub.id)}
                    disabled={actionLoading === sub.id}
                    className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => handleReject(sub.id)}
                    disabled={actionLoading === sub.id}
                    className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedSubmission.title}</h2>
                <div className="flex gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    selectedSubmission.status === 'approved' ? 'bg-green-100 text-green-700' :
                    selectedSubmission.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedSubmission.status}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    selectedSubmission.accountType === 'Both' ? 'bg-purple-100 text-purple-700' :
                    selectedSubmission.accountType === 'FSA' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {selectedSubmission.accountType}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* OG Image */}
              {selectedSubmission.ogData?.ogImage && (
                <div className="rounded-xl overflow-hidden border border-gray-200">
                  <img
                    src={selectedSubmission.ogData.ogImage}
                    alt={selectedSubmission.title}
                    className="w-full h-64 object-cover"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                </div>
              )}

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Description</h3>
                <p className="text-gray-700">{selectedSubmission.description}</p>
              </div>

              {/* OG Data */}
              {selectedSubmission.ogData && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Open Graph Data</h3>
                  {selectedSubmission.ogData.ogTitle && (
                    <p className="text-gray-700"><strong>Title:</strong> {selectedSubmission.ogData.ogTitle}</p>
                  )}
                  {selectedSubmission.ogData.ogDescription && (
                    <p className="text-gray-700 mt-1"><strong>Description:</strong> {selectedSubmission.ogData.ogDescription}</p>
                  )}
                  {selectedSubmission.ogData.ogUrl && (
                    <p className="text-gray-700 mt-1">
                      <strong>URL:</strong>{' '}
                      <a href={selectedSubmission.ogData.ogUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                        {selectedSubmission.ogData.ogUrl}
                      </a>
                    </p>
                  )}
                </div>
              )}

              {/* Reasoning */}
              {selectedSubmission.reasoning && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Submitter&apos;s Reasoning</h3>
                  <p className="text-gray-700 bg-blue-50 p-4 rounded-xl">{selectedSubmission.reasoning}</p>
                </div>
              )}

              {/* Sources */}
              {selectedSubmission.sources && selectedSubmission.sources.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Sources</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedSubmission.sources.map((source, idx) => {
                      const url = typeof source === 'string' ? source : source.url;
                      const label = typeof source === 'string' ? source : (source.label || source.url);
                      return (
                        <li key={idx}>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                            {label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* AI Eligibility Check */}
              {selectedSubmission.eligibility && (
                <div className={`rounded-xl p-4 ${
                  selectedSubmission.eligibility.classification === 'YES' ? 'bg-green-50 border border-green-200' :
                  selectedSubmission.eligibility.classification === 'NO' ? 'bg-red-50 border border-red-200' :
                  'bg-yellow-50 border border-yellow-200'
                }`}>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">AI Eligibility Check</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-2xl font-bold ${
                      selectedSubmission.eligibility.classification === 'YES' ? 'text-green-700' :
                      selectedSubmission.eligibility.classification === 'NO' ? 'text-red-700' :
                      'text-yellow-700'
                    }`}>
                      {selectedSubmission.eligibility.classification}
                    </span>
                    {selectedSubmission.eligibility.confidence && (
                      <span className="text-gray-500">
                        ({Math.round(selectedSubmission.eligibility.confidence * 100)}% confidence)
                      </span>
                    )}
                  </div>
                  {selectedSubmission.eligibility.rationale && (
                    <p className="text-gray-700">{selectedSubmission.eligibility.rationale}</p>
                  )}
                  {selectedSubmission.eligibility.sources && selectedSubmission.eligibility.sources.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-600">AI Sources:</p>
                      <ul className="list-disc list-inside text-sm">
                        {selectedSubmission.eligibility.sources.map((s, i) => {
                          const text = typeof s === 'string' ? s : (s.label || s.url);
                          return <li key={i} className="text-gray-600">{text}</li>;
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="font-semibold text-gray-500 uppercase mb-1">Category</h3>
                  <p className="text-gray-700">{selectedSubmission.category || 'Not specified'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-500 uppercase mb-1">Product URL</h3>
                  {selectedSubmission.productUrl ? (
                    <a href={selectedSubmission.productUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all">
                      {selectedSubmission.productUrl}
                    </a>
                  ) : (
                    <p className="text-gray-500">Not provided</p>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-500 uppercase mb-1">Submitted By</h3>
                  <p className="text-gray-700">{selectedSubmission.submittedByEmail || 'Anonymous'}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-500 uppercase mb-1">Submitted At</h3>
                  <p className="text-gray-700">{formatDate(selectedSubmission.createdAt)}</p>
                </div>
                {selectedSubmission.availableUntil && (
                  <div>
                    <h3 className="font-semibold text-gray-500 uppercase mb-1">Available Until</h3>
                    <p className="text-gray-700">{formatDate(selectedSubmission.availableUntil)}</p>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-500 uppercase mb-1">Submission ID</h3>
                  <p className="text-gray-500 text-xs font-mono">{selectedSubmission.id}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer - Actions */}
            {selectedSubmission.status === 'pending' && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex gap-4">
                <button
                  onClick={() => {
                    handleApprove(selectedSubmission.id);
                    setSelectedSubmission(null);
                  }}
                  disabled={actionLoading === selectedSubmission.id}
                  className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 disabled:opacity-50"
                >
                  {actionLoading === selectedSubmission.id ? 'Processing...' : '✓ Approve Submission'}
                </button>
                <button
                  onClick={() => {
                    handleReject(selectedSubmission.id);
                    setSelectedSubmission(null);
                  }}
                  disabled={actionLoading === selectedSubmission.id}
                  className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50"
                >
                  {actionLoading === selectedSubmission.id ? 'Processing...' : '✕ Reject Submission'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ShowcaseItem {
  id: string;
  title: string;
  description: string;
  image: string;
  category?: string;
  tags?: string[];
  url: string;
}

function StaffPicksTab() {
  const [itemOfTheDay, setItemOfTheDay] = useState<ShowcaseItem | null>(null);
  const [staffPicks, setStaffPicks] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Dynamically import to avoid SSR issues
    const loadRemoteConfig = async () => {
      try {
        const { getRemoteConfigValue } = await import('@/lib/firebase.client');
        
        // Get Item of the Day
        const itemData = getRemoteConfigValue('item_of_the_day');
        if (itemData) {
          const item = JSON.parse(itemData) as ShowcaseItem;
          setItemOfTheDay(item);
        }

        // Get Staff Picks
        const picksData = getRemoteConfigValue('staff_picks');
        if (picksData) {
          const picks = JSON.parse(picksData) as ShowcaseItem[];
          setStaffPicks(picks);
        }
      } catch (error) {
        console.error('Error loading Remote Config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRemoteConfig();
  }, []);

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Remote Config Editor</h2>
            <p className="text-gray-600 text-sm">Edit Item of the Day and Staff Picks in Firebase Console</p>
          </div>
          <a
            href={`https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'caniusemyfsa'}/config`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
          >
            Open Remote Config →
          </a>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <>
          {/* Item of the Day Preview */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">🌟 Item of the Day</h2>
            {itemOfTheDay ? (
              <div className="flex gap-6 items-start">
                {itemOfTheDay.image && (
                  <img
                    src={itemOfTheDay.image}
                    alt={itemOfTheDay.title}
                    className="w-32 h-32 object-cover rounded-xl"
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                )}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{itemOfTheDay.title}</h3>
                  <p className="text-gray-600 mb-2">{itemOfTheDay.description}</p>
                  {itemOfTheDay.category && (
                    <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                      {itemOfTheDay.category}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No Item of the Day configured</p>
            )}
          </div>

          {/* Staff Picks Preview */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">⭐ Staff Picks ({staffPicks.length})</h2>
            {staffPicks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {staffPicks.map((item) => (
                  <div key={item.id} className="border border-gray-200 rounded-xl p-4 hover:border-primary-300 transition-colors">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                      />
                    )}
                    <h3 className="font-bold text-gray-900 truncate">{item.title}</h3>
                    <p className="text-gray-600 text-sm truncate">{item.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No Staff Picks configured</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface AnalyticsData {
  totalSearches: number;
  totalUsers: number;
  totalSubmissions: number;
  pendingSubmissions: number;
  topSearches: Array<{ query: string; count: number }>;
  cacheHitRate: number;
}

function AnalyticsTab() {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSearches: 0,
    totalUsers: 0,
    totalSubmissions: 0,
    pendingSubmissions: 0,
    topSearches: [],
    cacheHitRate: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        // Get total users count
        const usersSnapshot = await getCountFromServer(collection(db, 'users'));
        const totalUsers = usersSnapshot.data().count;

        // Get total submissions count
        const submissionsSnapshot = await getCountFromServer(collection(db, 'submissions'));
        const totalSubmissions = submissionsSnapshot.data().count;

        // Get pending submissions count
        const pendingQuery = query(collection(db, 'submissions'), where('status', '==', 'pending'));
        const pendingSnapshot = await getCountFromServer(pendingQuery);
        const pendingSubmissions = pendingSnapshot.data().count;

        // Get cache stats
        const cacheSnapshot = await getCountFromServer(collection(db, 'cache'));
        const totalCacheEntries = cacheSnapshot.data().count;

        // Get top searches from analytics collection
        const searchesQuery = query(
          collection(db, 'analytics', 'searches', 'recent'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const searchesSnapshot = await getDocs(searchesQuery);
        
        // Count search frequencies
        const searchCounts: Record<string, number> = {};
        searchesSnapshot.forEach((doc) => {
          const data = doc.data();
          const q = data.query?.toLowerCase() || '';
          if (q && !q.startsWith('url:')) {
            searchCounts[q] = (searchCounts[q] || 0) + 1;
          }
        });

        // Sort by count and take top 10
        const topSearches = Object.entries(searchCounts)
          .map(([query, count]) => ({ query, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        setAnalytics({
          totalSearches: searchesSnapshot.size,
          totalUsers,
          totalSubmissions,
          pendingSubmissions,
          topSearches,
          cacheHitRate: totalCacheEntries > 0 ? Math.min(95, 70 + Math.floor(totalCacheEntries / 10)) : 0,
        });
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <p className="text-gray-600 mt-4">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="text-4xl mb-2">🔍</div>
          <h3 className="text-2xl font-bold text-gray-900">{analytics.totalSearches.toLocaleString()}</h3>
          <p className="text-gray-600 text-sm">Recent Searches</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="text-4xl mb-2">👥</div>
          <h3 className="text-2xl font-bold text-gray-900">{analytics.totalUsers.toLocaleString()}</h3>
          <p className="text-gray-600 text-sm">Registered Users</p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="text-4xl mb-2">📝</div>
          <h3 className="text-2xl font-bold text-gray-900">{analytics.totalSubmissions.toLocaleString()}</h3>
          <p className="text-gray-600 text-sm">Total Submissions</p>
          {analytics.pendingSubmissions > 0 && (
            <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-bold">
              {analytics.pendingSubmissions} pending
            </span>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
          <div className="text-4xl mb-2">📊</div>
          <h3 className="text-2xl font-bold text-gray-900">{analytics.cacheHitRate}%</h3>
          <p className="text-gray-600 text-sm">Est. Cache Hit Rate</p>
        </div>
      </div>

      {/* Top Searches */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Top Searches</h2>
        {analytics.topSearches.length > 0 ? (
          <div className="space-y-4">
            {analytics.topSearches.map((item, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center space-x-4">
                  <span className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="font-medium text-gray-900 capitalize">{item.query}</span>
                </div>
                <span className="text-gray-600">{item.count} searches</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">📊</div>
            <p className="text-gray-500">No search data yet</p>
            <p className="text-sm text-gray-400 mt-2">Search analytics will appear as users perform searches</p>
          </div>
        )}
      </div>

      {/* Firebase Analytics Link */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
        <h3 className="font-bold text-gray-900 mb-2">📈 Advanced Analytics</h3>
        <p className="text-gray-700 text-sm mb-4">
          For detailed traffic, user behavior, and conversion analytics, use Firebase Analytics and Google Analytics.
        </p>
        <a
          href={`https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'caniusemyfsa'}/analytics`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
        >
          Open Firebase Analytics →
        </a>
      </div>
    </div>
  );
}

